import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface DashboardHistoryItem {
  id: string;
  title: string;
  title_short?: string;
  jurisdiction_name: string;
  regulation_type?: string;
  status?: string;
  last_action_date?: string;
  effective_date?: string;
  viewedAt: string; // ISO timestamp
}

interface DashboardHistoryContextType {
  history: DashboardHistoryItem[];
  addToHistory: (item: Omit<DashboardHistoryItem, 'viewedAt'>) => void;
  clearHistory: () => void;
  removeFromHistory: (viewedAt: string) => void;
}

const DashboardHistoryContext = createContext<DashboardHistoryContextType | undefined>(undefined);

const STORAGE_KEY = 'dashboard_access_history';
const MAX_HISTORY_ITEMS = 20;

export const DashboardHistoryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [history, setHistory] = useState<DashboardHistoryItem[]>([]);

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setHistory(parsed);
      }
    } catch (error) {
      console.error('Error loading dashboard history:', error);
    }
  }, []);

  // Save history to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    } catch (error) {
      console.error('Error saving dashboard history:', error);
    }
  }, [history]);

  const addToHistory = (item: Omit<DashboardHistoryItem, 'viewedAt'>) => {
    setHistory(prev => {
      // Remove duplicate if exists
      const filtered = prev.filter(h => h.id !== item.id);
      
      // Add new item at the beginning
      const newHistory = [
        { ...item, viewedAt: new Date().toISOString() },
        ...filtered
      ];
      
      // Keep only the most recent items
      return newHistory.slice(0, MAX_HISTORY_ITEMS);
    });
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  const removeFromHistory = (viewedAt: string) => {
    setHistory(prev => prev.filter(item => item.viewedAt !== viewedAt));
  };

  return (
    <DashboardHistoryContext.Provider value={{ history, addToHistory, clearHistory, removeFromHistory }}>
      {children}
    </DashboardHistoryContext.Provider>
  );
};

export const useDashboardHistory = () => {
  const context = useContext(DashboardHistoryContext);
  if (!context) {
    throw new Error('useDashboardHistory must be used within DashboardHistoryProvider');
  }
  return context;
};
